/**
 * PowerSync Bridge — Renderer Process
 *
 * Thin wrapper around the local @powersync/web database instance.
 * Exports the same function/hook signatures as the old IPC-based bridge
 * so all callers (localOrderOps.ts, RunningOrderLiveSync.tsx, etc.) require
 * no changes.
 */

import { useEffect, useRef } from 'react';

import type {
  PowerSyncChangePayload,
  PowerSyncExecuteResult,
  PowerSyncQueryAllResult,
  PowerSyncQueryResult,
  PowerSyncStatusData,
  PowerSyncStatusResult,
  RawPowerSyncStatus,
} from '@/renderer/types/powersync';
import { extractSyncErrorMessage } from '@/renderer/types/powersync';

import { db } from './database';
import { syncEvents } from './supabaseConnector';

// ── "No outlet is active yet" watchdog ──────────────────────────────────────
//
// Main briefly has no active outlet while an outlet activation is in flight
// (session hydration, IPC round-trip, local database open) — normal, and
// individual PowerSync operations already tolerate it. But if it persists,
// something is genuinely stuck (e.g. outlet context was never pushed, or the
// local database repeatedly fails to recover) and the renderer would
// otherwise just keep silently swallowing errors and showing empty screens.
// Track how long this specific failure has been continuous; past a grace
// period, prompt the app to sign the user out and back in rather than
// leaving them stuck indefinitely.
const NO_ACTIVE_OUTLET_PATTERN = /No outlet is active yet/i;
const NO_ACTIVE_OUTLET_GRACE_MS = 8000;
let noActiveOutletSince: number | null = null;
let outletUnavailableFired = false;

function trackNoActiveOutlet(message: string | null): void {
  if (message && NO_ACTIVE_OUTLET_PATTERN.test(message)) {
    const now = Date.now();
    if (noActiveOutletSince === null) noActiveOutletSince = now;
    if (
      !outletUnavailableFired &&
      now - noActiveOutletSince > NO_ACTIVE_OUTLET_GRACE_MS
    ) {
      outletUnavailableFired = true;
      syncEvents.dispatchEvent(new CustomEvent('outlet-unavailable'));
    }
  } else {
    noActiveOutletSince = null;
    outletUnavailableFired = false;
  }
}

// ── Local database circuit breaker ──────────────────────────────────────────
//
// If the local WASM SQLite engine's underlying file is corrupted/unopenable
// (e.g. a stale IndexedDB left over from a previous broken session on this
// origin), every query against it fails immediately with the same class of
// error. Without a breaker, every call site — on every mount, every table-
// change event, every realtime push, every periodic reconnect — keeps
// hammering the same broken engine forever, each failure logging a full
// SQL+params object via console.error. Since this engine runs on the main
// thread (see database.ts's `useWebWorker: false`), that unbounded volume
// competes directly with rendering and input handling on the one thread the
// UI depends on, and can make the whole tab unresponsive.
//
// Trip after a burst of consecutive open/query failures and stop touching
// `db` entirely for a cooldown, giving database.ts's self-heal (delete +
// recreate the local database, see recoverLocalDatabase()) a clear signal
// and a clear run at fixing things instead of racing fresh failures.
const DB_CORRUPTION_PATTERN =
  /sqlite3_open_v2|sqlite.*open|unable to open database|database.*disk image is malformed/i;
const DB_FAILURE_THRESHOLD = 5;
const DB_FAILURE_WINDOW_MS = 5000;
const DB_BREAKER_COOLDOWN_MS = 15000;
let dbFailureTimestamps: number[] = [];
let breakerTrippedUntil = 0;
let unrecoverableFired = false;

function isBreakerOpen(): boolean {
  return Date.now() < breakerTrippedUntil;
}

function trackDbFailure(message: string | null): void {
  if (!message || !DB_CORRUPTION_PATTERN.test(message)) return;
  const now = Date.now();
  dbFailureTimestamps.push(now);
  dbFailureTimestamps = dbFailureTimestamps.filter(
    (t) => now - t <= DB_FAILURE_WINDOW_MS,
  );
  if (
    dbFailureTimestamps.length >= DB_FAILURE_THRESHOLD &&
    !unrecoverableFired
  ) {
    unrecoverableFired = true;
    breakerTrippedUntil = now + DB_BREAKER_COOLDOWN_MS;
    syncEvents.dispatchEvent(new CustomEvent('local-db-unrecoverable'));
  }
}

function resetDbBreaker(): void {
  if (dbFailureTimestamps.length === 0 && !unrecoverableFired) return;
  dbFailureTimestamps = [];
  breakerTrippedUntil = 0;
  unrecoverableFired = false;
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Run an INSERT / UPDATE / DELETE statement.
 * Returns { success, rowsAffected } or { success: false, error }.
 */
export async function psExecute(
  sql: string,
  params?: unknown[],
): Promise<PowerSyncExecuteResult> {
  if (isBreakerOpen()) {
    return { success: false, error: 'Local database is recovering' };
  }
  try {
    const result = await db.execute(sql, params as unknown[]);
    trackNoActiveOutlet(null);
    resetDbBreaker();
    return { success: true, rowsAffected: result.rowsAffected ?? 0 };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    trackNoActiveOutlet(message);
    trackDbFailure(message);
    console.error('[PowerSync] execute error:', message, { sql });
    return { success: false, error: message };
  }
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/**
 * SELECT returning at most one row, or null if nothing matched.
 */
export async function psGet<T = Record<string, unknown>>(
  sql: string,
  params?: unknown[],
): Promise<T | null> {
  if (isBreakerOpen()) return null;
  try {
    const row = await db.get<T>(sql, params as unknown[]);
    trackNoActiveOutlet(null);
    resetDbBreaker();
    return row ?? null;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    // PowerSync throws when no row is found — treat that as null, not an error.
    if (/no results|result set is empty/i.test(message)) {
      trackNoActiveOutlet(null);
      resetDbBreaker();
      return null;
    }
    trackNoActiveOutlet(message);
    trackDbFailure(message);
    console.error('[PowerSync] get error:', message, { sql });
    return null;
  }
}

/**
 * SELECT returning all matching rows (empty array if none found).
 */
export async function psGetAll<T = Record<string, unknown>>(
  sql: string,
  params?: unknown[],
): Promise<T[]> {
  if (isBreakerOpen()) return [];
  try {
    const rows = await db.getAll<T>(sql, params as unknown[]);
    trackNoActiveOutlet(null);
    resetDbBreaker();
    return rows;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    trackNoActiveOutlet(message);
    trackDbFailure(message);
    console.error('[PowerSync] getAll error:', message, { sql });
    return [];
  }
}

/**
 * Returns the current sync status snapshot.
 */
export async function psStatus(): Promise<PowerSyncStatusData> {
  const s = db.currentStatus;
  return {
    connected: s.connected,
    connecting: s.connecting ?? false,
    status: s.connected
      ? 'connected'
      : (s.connecting ?? false)
        ? 'connecting'
        : 'disconnected',
    lastSyncedAt: s.lastSyncedAt?.toISOString() ?? null,
    hasSynced: s.hasSynced ?? false,
    uploadQueueSize: s.dataFlowStatus?.uploading ? 1 : 0,
    downloading: s.dataFlowStatus?.downloading ?? false,
    downloadError: extractSyncErrorMessage(s.dataFlowStatus?.downloadError),
    uploadError: extractSyncErrorMessage(s.dataFlowStatus?.uploadError),
  };
}

// ── React hooks ───────────────────────────────────────────────────────────────

/**
 * Subscribes to PowerSync table-change events and calls `callback` whenever
 * any of the listed tables are mutated. Cleans up the listener automatically
 * when the component unmounts.
 *
 * Providing an empty `tables` array subscribes to ALL table changes.
 */
export function usePowerSyncChange(
  tables: string[],
  callback: (changedTables: string[]) => void,
): void {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const tablesKey = tables.join(',');

  useEffect(() => {
    const cleanup = db.onChange(
      {
        onChange(event) {
          if (tables.length === 0) {
            callbackRef.current(event.changedTables);
            return;
          }
          const relevant = event.changedTables.filter((t) =>
            tables.includes(t),
          );
          if (relevant.length > 0) callbackRef.current(relevant);
        },
      },
      { tables },
    );
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablesKey]);
}

/**
 * Subscribes to PowerSync sync-status push events.
 */
function toStatusData(s: RawPowerSyncStatus): PowerSyncStatusData {
  return {
    connected: s.connected,
    connecting: s.connecting ?? false,
    status: s.connected
      ? 'connected'
      : (s.connecting ?? false)
        ? 'connecting'
        : 'disconnected',
    lastSyncedAt: s.lastSyncedAt?.toISOString() ?? null,
    hasSynced: s.hasSynced ?? false,
    uploadQueueSize: s.dataFlowStatus?.uploading ? 1 : 0,
    downloading: s.dataFlowStatus?.downloading ?? false,
    downloadError: extractSyncErrorMessage(s.dataFlowStatus?.downloadError),
    uploadError: extractSyncErrorMessage(s.dataFlowStatus?.uploadError),
  };
}

export function usePowerSyncStatus(
  callback: (status: PowerSyncStatusData) => void,
): void {
  useEffect(() => {
    const cleanup = db.registerListener({
      statusChanged(s) {
        callback(toStatusData(s));
      },
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ── Legacy type re-exports (kept for consumers that import from here) ──────────

export type {
  PowerSyncChangePayload,
  PowerSyncExecuteResult,
  PowerSyncQueryAllResult,
  PowerSyncQueryResult,
  PowerSyncStatusResult,
};
